import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';
import { supabase } from './supabaseClient';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

export class ServiceExecutiveService {
  static async uploadComplaintMedia(files = [], executiveId = '') {
    if (!files.length) {
      return { success: true, urls: [], error: null };
    }
    try {
      const uploads = await Promise.all(
        files.map(async (file) => {
          const fileExt = file.name?.split('.').pop() || 'jpg';
          const safeExecId = executiveId || 'unknown';
          const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
          const filePath = `registration/${safeExecId}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('complaint-media')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: file.type || undefined,
            });

          if (uploadError) {
            throw uploadError;
          }

          const { data } = supabase.storage.from('complaint-media').getPublicUrl(filePath);
          return data?.publicUrl || null;
        })
      );

      return {
        success: true,
        urls: uploads.filter(Boolean),
        error: null,
      };
    } catch (error) {
      console.error('Error uploading complaint media:', error);
      return {
        success: false,
        urls: [],
        error: error.message || 'Media upload failed',
      };
    }
  }

  static async getDashboardStats(executiveId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_get_dashboard_stats`, {
        method: 'POST',
        body: JSON.stringify({ p_executive_id: executiveId }),
      });
      return { success: true, data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  static async getServiceRequests(executiveId, status = '', page = 1, limit = 20) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_get_requests`, {
        method: 'POST',
        body: JSON.stringify({
          p_executive_id: executiveId,
          p_status: status || '',
          p_page: page,
          p_limit: limit,
        }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching service requests:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async searchServiceRequests(executiveId, searchTerm, status = '', page = 1, limit = 20) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_search_requests`, {
        method: 'POST',
        body: JSON.stringify({
          p_executive_id: executiveId,
          p_search_term: searchTerm,
          p_status: status || '',
          p_page: page,
          p_limit: limit,
        }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error searching service requests:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async updateServiceRequestStatus(requestId, newStatus, executiveId, remark = '') {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_update_status`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_new_status: newStatus,
          p_executive_id: executiveId,
          p_remark: remark || '',
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return {
        success: result?.success ?? true,
        message: result?.message || 'Status updated successfully',
        data: result || null,
        error: null,
      };
    } catch (error) {
      console.error('Error updating service request status:', error);
      return { success: false, message: error.message || 'Failed to update status', error: error.message };
    }
  }

  static async getServiceRequestById(requestId) {
    try {
      const data = await supabaseRequest(`/rest/v1/service_requests?id=eq.${requestId}&select=*`, {
        method: 'GET',
      });
      return { success: true, data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('Error fetching service request by id:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  static async getServiceRequestStatusHistory(requestId) {
    try {
      const data = await supabaseRequest(
        `/rest/v1/service_request_status_history?service_request_id=eq.${requestId}&order=changed_at.desc`,
        { method: 'GET' }
      );
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching service request status history:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getComplaints(executiveId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_get_complaints`, {
        method: 'POST',
        body: JSON.stringify({ p_executive_id: executiveId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching complaints:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  // Create new service request - matches mobile payload shape
  static async createServiceRequest(requestData, executiveId, executiveName) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_create_request`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_data: {
            customer_name: requestData.customer_name,
            vehicle_number: requestData.vehicle_number,
            service_type: requestData.service_type,
            priority: requestData.priority,
            contact_number: requestData.contact_number,
            alternate_number: requestData.alternate_number || '',
            problem: requestData.problem,
            address: requestData.address || '',
            pincode: requestData.pincode || '',
            customer_quoted_amount: requestData.customer_quoted_amount,
            advance_payment: requestData.advance_payment || '',
            service_tag: requestData.service_tag || '',
            location_link: requestData.location_link || '',
            media_upload: requestData.media_upload || [],
            source: requestData.source,
            remark: requestData.remark || '',
            is_premium: requestData.is_premium || false,
            card_number: requestData.card_number || '',
            registered_by_executive_id: executiveId,
            registered_by_executive_name: executiveName,
            request_status: 'pending',
            complaint_status: 'registered',
            drop_location: requestData.drop_location || '',
          },
        }),
      });

      if (data && data.length > 0) {
        const result = data[0];
        return {
          success: result.success,
          message: result.message,
          requestId: result.id,
        };
      }

      return { success: false, message: 'Failed to create service request' };
    } catch (error) {
      console.error('Error creating service request:', error);
      return { success: false, message: error.message || 'Internal server error' };
    }
  }

  // Send WhatsApp location request - matches mobile behavior
  static async sendWhatsAppLocationRequest(phoneNumber) {
    try {
      const response = await fetch('https://public.doubletick.io/whatsapp/message/template', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: 'key_ZUfIHHuFPQ',
        },
        body: JSON.stringify({
          messages: [
            {
              to: `91${phoneNumber}`,
              content: {
                templateName: 'cust_location',
                language: 'en',
                templateData: {
                  body: { placeholders: [] },
                },
              },
            },
          ],
        }),
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing WhatsApp response:', parseError);
        result = { message: 'Invalid response from WhatsApp API' };
      }

      if (response.ok) {
        return { success: true, message: 'Location request sent successfully via WhatsApp' };
      }

      return {
        success: false,
        message: `Failed to send location request: ${result.message || 'Unknown error'}`,
      };
    } catch (error) {
      console.error('WhatsApp location request error:', error);
      return {
        success: false,
        message: 'Failed to send location request. Please check your internet connection.',
      };
    }
  }

  static async updateWhatsAppLocationSent(requestId) {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/service_requests?id=eq.${requestId}`, {
        method: 'PATCH',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({
          whatsapp_location_sent: true,
          whatsapp_location_sent_at: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || 'Request failed');
      }

      return { success: true, data: null };
    } catch (error) {
      console.error('Error updating WhatsApp location sent:', error);
      return { success: false, error: error.message };
    }
  }

  static async getOrders(executiveId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_get_orders`, {
        method: 'POST',
        body: JSON.stringify({ p_executive_id: executiveId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async updateOrderStatus(orderId, status) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_update_order_status`, {
        method: 'POST',
        body: JSON.stringify({ p_order_id: orderId, p_status: status }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error updating order status:', error);
      return { success: false, error: error.message };
    }
  }
}

